// LUMINA · pages/vajra/VajraTeachersPage.jsx
// Phase 70: Dedicated teachers listing page

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, GD, GDL, GDH, BG, TXT, TXT2 } from './constants';

// Tradition definitions
const TRADITIONS = [
  { key:"gelug",         zh:"格鲁",     en:"Gelug",  tooltip:"菩提道次第传承，以辩经闻思为基" },
  { key:"nyingma",       zh:"宁玛",     en:"Nyingma", tooltip:"大圆满心髓传承，直指心性" },
  { key:"drikung_kagyu", zh:"止贡噶举", en:"Drikung", tooltip:"大手印与那若六法传承" },
  { key:"sakya",         zh:"萨迦",     en:"Sakya",  tooltip:"道果(LamDre)传承，轮涅无别" },
  { key:"jonang",        zh:"觉囊",     en:"Jonang", tooltip:"他空见传承，时轮金刚" },
];

export default function VajraTeachersPage({ setPage, member, lang: langProp, T }) {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const lang = langProp || localStorage.getItem("lumina_lang") || "zh";
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetch(`${API}/vajra/teachers`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setTeachers(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const L = (zh, en) => lang === "zh" ? zh : en;

  if (loading) return (
    <div style={{minHeight:"100vh",background:BG,display:"flex",alignItems:"center",justifyContent:"center",color:GD,fontSize:14,letterSpacing:2}}>
      {L("载入中…","Loading…")}
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:BG,fontFamily:"'Noto Serif SC','Noto Serif',serif",color:TXT}}>
      {/* Content */}
      <div style={{maxWidth:1080,margin:"0 auto",padding:"40px 24px 80px"}}>
        {/* Page title */}
        <div style={{textAlign:"center",marginBottom:48}}>
          <div style={{fontSize:"clamp(24px,5vw,36px)",color:GD,letterSpacing:"0.15em",
            fontFamily:"'Noto Serif SC',serif",fontWeight:500,marginBottom:12}}>
            {L("传承上师","Lineage Teachers")}
          </div>
          <div style={{fontSize:13,color:TXT2,letterSpacing:1.5,lineHeight:1.8,maxWidth:600,margin:"0 auto"}}>
            {L(
              "以下为如意宝金刚社群传承上师简介。",
              "BTF Canada Vajra Community serves five Vajrayana lineages. Below are introductions to our qualified teachers."
            )}
          </div>
        </div>

        {/* Teachers grid */}
        {teachers.length === 0 ? (
          <div style={{textAlign:"center",padding:"60px 0",color:TXT2,fontSize:13}}>
            {L("上师资料整理中，敬请关注。","Teacher profiles coming soon.")}
          </div>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:20}}>
            {teachers.map((t, i) => {
              const isOpen = expanded === (t.id || i);
              const traditionInfo = TRADITIONS.find(tr => tr.key === t.tradition);
              return (
                <div key={t.id || i} style={{
                  background:"#ffffff",border:"1px solid rgba(212,175,55,0.15)",
                  borderRadius:16,padding:"24px 22px",position:"relative",overflow:"hidden",
                  boxShadow:"0 2px 8px rgba(0,0,0,0.06)",
                  transition:"transform .2s,box-shadow .2s",
                }}
                  onMouseOver={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(212,175,55,0.12)";}}
                  onMouseOut={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,0.06)";}}>
                  {/* Decorative ☸ */}
                  <div style={{position:"absolute",top:14,right:16,fontSize:36,opacity:.08}}>☸</div>

                  {/* Tradition badge */}
                  <div style={{fontSize:10,color:GD,letterSpacing:2,textTransform:"uppercase",marginBottom:10,fontWeight:600}}>
                    {traditionInfo ? L(traditionInfo.zh, traditionInfo.en) : (t.tradition || "")}
                  </div>

                  {/* Name */}
                  <div style={{fontSize:20,color:"#846B32",letterSpacing:1,marginBottom:3,fontWeight:600,
                    fontFamily:"'Noto Serif SC',serif"}}>
                    {t.name_zh}
                  </div>
                  {t.name_en && (
                    <div style={{fontSize:11,color:"#6B5030",marginBottom:12,fontStyle:"italic"}}>
                      {t.name_en}{t.lineage ? ` · ${t.lineage}` : ""}
                    </div>
                  )}

                  {/* Tradition tooltip */}
                  {traditionInfo && (
                    <div style={{fontSize:11,color:TXT2,lineHeight:1.6,marginBottom:10,
                      padding:"6px 10px",background:"rgba(139,105,20,0.04)",borderRadius:8,
                      border:"1px solid rgba(139,105,20,0.08)"}}>
                      {L(traditionInfo.tooltip, traditionInfo.tooltip)}
                    </div>
                  )}

                  {/* Bio */}
                  <div style={{fontSize:12,color:TXT,lineHeight:1.8,
                    display:"-webkit-box",WebkitLineClamp:isOpen?99:3,WebkitBoxOrient:"vertical",
                    overflow:"hidden",marginBottom:8}}>
                    {t.bio_zh || t.bio_en || L("简介待补充","Bio coming soon")}
                  </div>

                  {/* Expand toggle */}
                  {(t.bio_zh || t.bio_en) && (
                    <button onClick={() => setExpanded(isOpen ? null : (t.id || i))} style={{
                      background:"none",border:"none",color:GDL,fontSize:11,cursor:"pointer",
                      padding:"2px 0",letterSpacing:.3}}>
                      {isOpen ? L("收起 ▲","Collapse ▲") : L("了解更多 ▼","Read more ▼")}
                    </button>
                  )}

                  {/* Link to library filtered by teacher */}
                  {t.id && !String(t.id).startsWith("fb") && (
                    <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(139,105,20,0.08)"}}>
                      <a href={`/vajra?tab=teachings&teacher_id=${t.id}`} style={{
                        fontSize:11,color:GDL,textDecoration:"none",letterSpacing:.3}}>
                        📚 {L("查看该上师法教 →","View teachings →")}
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer tiny */}
        <div style={{textAlign:"center",marginTop:64,paddingTop:24,borderTop:"1px solid rgba(212,175,55,0.12)"}}>
            <a href="/vajra" style={{fontSize:12,color:GDL,textDecoration:"none",letterSpacing:.5}}>
              如意宝金刚社群
            </a>
        </div>
      </div>
    </div>
  );
}
